import React, { useState, useEffect } from 'react';
import { FileText, Upload, Download, Calendar, DollarSign, CheckCircle } from 'lucide-react';
import { professionalClientService } from '../../../services/professionalClientService';

const DocumentsTab = ({ clientId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeDocType, setActiveDocType] = useState('all');

  useEffect(() => {
    loadDocuments();
  }, [clientId, activeDocType]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const docType = activeDocType === 'all' ? null : activeDocType;
      const data = await professionalClientService?.getClientDocuments(clientId, docType);
      setDocuments(data);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event, documentType) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      await professionalClientService?.uploadDocument(clientId, file, {
        documentType,
        status: 'pending'
      });
      await loadDocuments();
      alert('Document uploadé avec succès');
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Erreur lors de l\'upload du document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (document) => {
    try {
      const signedUrl = await professionalClientService?.getDocumentSignedUrl(document?.fileUrl);
      if (signedUrl) {
        window.open(signedUrl, '_blank');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Erreur lors du téléchargement');
    }
  };

  const docTypes = [
    { id: 'all', label: 'Tous les documents' },
    { id: 'invoice', label: 'Factures' },
    { id: 'ex1_certificate', label: 'Certificats EX1' },
    { id: 'photo', label: 'Photos' },
    { id: 'other', label: 'Autres documents' }
  ];

  const getDocTypeIcon = (type) => {
    switch (type) {
      case 'invoice':
        return <DollarSign className="w-5 h-5 text-green-600" />;
      case 'ex1_certificate':
        return <CheckCircle className="w-5 h-5 text-blue-600" />;
      case 'photo':
        return <FileText className="w-5 h-5 text-purple-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        <p className="ml-4 text-gray-600">Chargement des documents...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Documents</h2>
      </div>
      {/* Document Type Tabs */}
      <div className="flex items-center gap-2 mb-6 border-b border-gray-200">
        {docTypes?.map((type) => (
          <button
            key={type?.id}
            onClick={() => setActiveDocType(type?.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeDocType === type?.id
                ? 'border-green-600 text-green-600' :'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {type?.label}
          </button>
        ))}
      </div>
      {/* Upload Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block">
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => handleFileUpload(e, 'invoice')}
              disabled={uploading}
              className="hidden"
            />
            <div className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 cursor-pointer transition-colors">
              <Upload className="w-5 h-5 mr-2 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Uploader une facture</span>
            </div>
          </label>
        </div>
        <div>
          <label className="block">
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => handleFileUpload(e, 'ex1_certificate')}
              disabled={uploading}
              className="hidden"
            />
            <div className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 cursor-pointer transition-colors">
              <Upload className="w-5 h-5 mr-2 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Uploader un certificat EX1</span>
            </div>
          </label>
        </div>
        <div>
          <label className="block">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileUpload(e, 'photo')}
              disabled={uploading}
              className="hidden"
            />
            <div className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 cursor-pointer transition-colors">
              <Upload className="w-5 h-5 mr-2 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Uploader des photos</span>
            </div>
          </label>
        </div>
      </div>
      {/* Documents List */}
      {documents?.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600">Aucun document trouvé</p>
        </div>
      ) : (
        <div className="space-y-3">
          {documents?.map((doc) => (
            <div key={doc?.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-green-500 transition-colors">
              <div className="flex items-center gap-3">
                {getDocTypeIcon(doc?.documentType)}
                <div>
                  <p className="font-medium text-gray-900">{doc?.fileName}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                    {doc?.documentNumber && <span>N° {doc?.documentNumber}</span>}
                    {doc?.documentDate && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(doc?.documentDate)?.toLocaleDateString('fr-FR')}
                      </div>
                    )}
                    {doc?.amountTtc && <span>{doc?.amountTtc}€ TTC</span>}
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleDownload(doc)}
                className="inline-flex items-center px-3 py-1.5 text-sm text-green-600 hover:text-green-800 font-medium"
              >
                <Download className="w-4 h-4 mr-1" />
                Télécharger
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;